import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { whoami, getLastLogin } from "../services/APIClient";

// Teachers whose last login predates the 2026/27 role-update rollout must
// actively re-confirm their roles for the new school year.
const THRESHOLD_DATE = new Date("2026-07-30T00:00:00Z");

const emptyState = {
  needsRoleSelection: false,
  teacherid: null,
};

const RoleSelectionContext = createContext({
  ...emptyState,
  loading: false,
  refresh: () => Promise.resolve(),
});

export function RoleSelectionProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState(emptyState);
  const [loadedToken, setLoadedToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoadedToken(null);
      setState(emptyState);
      return;
    }
    setLoading(true);
    try {
      const identity = await whoami();
      const stakeholder = identity.data.stakeholder;
      const additionalRoles = stakeholder.additionalRoles || [];

      // Hard invariant: a teacher can never operate the app with zero roles,
      // regardless of when they last logged in.
      const hasNoRoles = additionalRoles.length === 0;

      const lastLoginData = await getLastLogin(stakeholder.id);
      const lastLogin = lastLoginData ? new Date(lastLoginData.lastloggedin) : null;
      const isStale = !lastLogin || lastLogin < THRESHOLD_DATE;

      setState({
        needsRoleSelection: hasNoRoles || isStale,
        teacherid: stakeholder.id,
      });
      setLoadedToken(token);
    } catch (error) {
      console.error("Failed to resolve role selection status:", error);
      // Fail open - don't lock teachers out of the app over a network blip.
      setState(emptyState);
      setLoadedToken(token);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token !== loadedToken) load();
  }, [load, loadedToken, location.pathname]);

  const value = useMemo(
    () => ({ ...state, loading, refresh: load }),
    [state, loading, load]
  );

  return (
    <RoleSelectionContext.Provider value={value}>
      {children}
    </RoleSelectionContext.Provider>
  );
}

export function useRoleSelection() {
  return useContext(RoleSelectionContext);
}
